import { useEffect, useMemo } from 'react';

import { useAuthentication } from '@givebacks/api';
import { useShowCauseW_Selector } from '@givebacks/api/core';
import {
  type ConsumerRedeemableTicket,
  listConsumerPostCheckoutRedeemableTicketsQuery,
} from '@givebacks/api/store';
import { useQuery } from '@tanstack/react-query';
import { EventTicket, EventTicketInfo, EventTicketsPrintButton } from 'core/Components';
import { usePlatformService } from 'core/Hooks';
import { getErrorMessage_v2 } from 'core/Utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Box,
  Center,
  Group,
  ImageMantine,
  LoadingOverlay,
  Stack,
  Text,
  Title,
  notifications,
} from 'ui';

import TicketActionButtons from './TicketActionButtons';
import AlertMessage from './TicketsAlertMessage';
import classes from './TicketsPage.module.css';
import { TicketsPageLayout } from './TicketsPageLayout/TicketsPageLayout';
import { makeTicketInfo } from './utils';

type TicketsPageProps = {
  redeemableTickets?: ConsumerRedeemableTicket[];
  isLoading?: boolean;
  cartId?: string;
  checkoutId?: string;
  email?: string;
};

type TicketsListForCartProps = {
  tickets: ConsumerRedeemableTicket[];
};

type TicketInfoProps = {
  ticket: ConsumerRedeemableTicket;
  ticketCount: number;
  index: number;
};
const TicketInfo = ({ ticket: baseTicket, ticketCount, index }: TicketInfoProps) => {
  const ticket = useMemo(() => {
    return makeTicketInfo(baseTicket);
  }, [baseTicket]);
  if (!ticket) return null;
  return (
    <EventTicket
      key={ticket?.ticketId}
      className={classes.TicketsPage__ticket}
      eventThumbnail={ticket?.eventThumbnail}
      title={ticket?.eventTitle}
      subtitle={ticket?.eventDateTitle}
      eventStartDate={ticket?.eventStartDate}
      venueName={ticket?.venueName}
      addressLineOne={ticket?.addressLineOne}
      addressLineTwo={ticket?.addressLineTwo}
      city={ticket?.city}
      state={ticket?.state}
      zipCode={ticket?.zipCode}
      totalTickets={ticketCount}
      ticketId={ticket?.ticketId}
      ticketName={ticket?.ticketName}
      ticketNumber={index + 1}
      isExpired={ticket?.isExpired}
      isUsed={ticket?.isUsed}
      isDeleted={ticket?.isDeleted}
      ticketQrCodeUrl={ticket?.ticketQrCodeUrl}
      ticketUrl={ticket?.ticketUrl}
    />
  );
};

const TicketsListForCart = ({ tickets }: TicketsListForCartProps) => {
  return (
    <Stack>
      {tickets.map((ticket, index) => {
        return (
          <TicketInfo
            key={ticket.uuid}
            ticket={ticket}
            ticketCount={tickets.length}
            index={index}
          />
        );
      })}
    </Stack>
  );
};

const CheckoutCartTitle = ({ causeId, cartId }: { causeId: string; cartId: string }) => {
  const { data: cause } = useShowCauseW_Selector({ uuid: causeId });
  if (!cause) return null;
  return (
    <Group>
      <ImageMantine
        src={cause?.logo?.url}
        alt={cause?.name}
        radius={12}
        w={48}
        style={{ aspectRatio: 1 / 1 }}
      />
      <Stack gap="4px">
        <Text fw={600} fz="14px">
          {cause?.name}
        </Text>
        <Text c="gray.6" fz="12px">
          Order {cartId}
        </Text>
      </Stack>
    </Group>
  );
};

/** Renders a list of tickets from a given cart id in a stack with the cart id as the title */
const CheckoutCartTicketsList = ({
  cartId,
  tickets,
}: {
  cartId: string;
  tickets: ConsumerRedeemableTicket[];
}) => {
  const causeId = useMemo(() => {
    return tickets[0]?.store_item?.cause_id;
  }, [tickets]);
  if (!causeId) return null;
  return (
    <Stack>
      <CheckoutCartTitle causeId={causeId} cartId={cartId} />
      <TicketsListForCart tickets={tickets} />
    </Stack>
  );
};

/** Renders the tickets grouped by cartID  in a stack */
const AllCheckoutTicketsList = ({
  ticketsByCartId,
}: {
  ticketsByCartId: [string, ConsumerRedeemableTicket[]][];
}) => {
  return (
    <Stack gap="lg">
      {ticketsByCartId.map(([cartId, tickets]) => {
        return <CheckoutCartTicketsList key={cartId} cartId={cartId} tickets={tickets} />;
      })}
    </Stack>
  );
};

const TicketsPage = ({
  redeemableTickets,
  isLoading,
  cartId,
  checkoutId,
  email,
}: TicketsPageProps) => {
  const { isAuthenticated } = useAuthentication();

  const Platform = usePlatformService();

  const ticketCount = redeemableTickets?.length;

  // Group redeemable tickets by Cart Id
  const ticketsByCartId = useMemo(() => {
    const events: Map<string, ConsumerRedeemableTicket[]> = new Map();
    redeemableTickets?.forEach((ticket) => {
      const eventCart = events.get(ticket.cart_id);
      if (eventCart) {
        eventCart.push(ticket);
      } else {
        events.set(ticket.cart_id, [ticket]);
      }
    });
    return Array.from(events.entries());
  }, [redeemableTickets]);

  const redeemableTicketList = useMemo(() => {
    return redeemableTickets?.map((ticket) => {
      return makeTicketInfo(ticket);
    });
  }, [redeemableTickets]);

  /* Hide download button on mobile devices and when there are no redeemable tickets */
  const showDownloadButton = useMemo(() => {
    return Platform.isDesktopBrowser && redeemableTicketList?.length;
  }, [Platform, redeemableTicketList]);

  const pdfTicketList = useMemo(() => {
    return redeemableTicketList
      ?.map((ticket) => {
        if (!ticket) return null;

        return {
          ...ticket,
          subtitle: ticket.eventDateTitle,
        };
      })
      .filter(Boolean) as EventTicketInfo[];
  }, [redeemableTicketList]);

  const displayOrderNumber = useMemo(() => {
    return checkoutId ?? cartId;
  }, [checkoutId, cartId]);

  const hasMoreThanOneCart = useMemo(() => {
    return ticketsByCartId.length > 1;
  }, [ticketsByCartId]);

  // This is a guard clause to prevent the component from rendering if neither checkout id nor cart id is provided although the parent component should handle this.
  if (!displayOrderNumber) {
    return null;
  }
  return (
    <TicketsPageLayout
      top={<AlertMessage isAuthenticated={isAuthenticated} />}
      title={
        hasMoreThanOneCart ? null : (
          <Box className={classes.TicketsPage__header}>
            <Title order={2}>Order {displayOrderNumber}</Title>
            <Badge>
              {ticketCount} {ticketCount === 1 ? 'Ticket' : 'Tickets'}
            </Badge>
          </Box>
        )
      }
      left={
        <Stack gap="xs" w="full">
          <TicketActionButtons
            isAuthenticated={isAuthenticated}
            email={email}
            cartId={cartId}
            checkoutId={checkoutId}
            DownloadButton={
              showDownloadButton ? (
                <EventTicketsPrintButton
                  title={`Order ${displayOrderNumber}`}
                  eventList={pdfTicketList}
                  buttonProps={{ visibleFrom: 'sm' }}
                />
              ) : null
            }
          />
        </Stack>
      }
      right={
        redeemableTicketList?.length ? (
          <AllCheckoutTicketsList ticketsByCartId={ticketsByCartId} />
        ) : (
          <Center>
            {isLoading ? (
              <LoadingOverlay visible={isLoading} />
            ) : (
              <Stack gap="xs" align="center">
                <Title order={3}>No tickets found</Title>
                <Text type="sub">Please check your order details and try again.</Text>
              </Stack>
            )}
          </Center>
        )
      }
    />
  );
};

type RenderWithPostCheckoutIdProps = {
  checkoutId?: string;
  email?: string;
  cartId?: string;
};

/**
 * Renders the tickets page with the redeemable tickets
 * If checkout id or cart id is provided, it will fetch the redeemable tickets
 */
const RenderWithPostCheckoutId = ({ checkoutId, email, cartId }: RenderWithPostCheckoutIdProps) => {
  const {
    data: redeemableTickets,
    isError,
    error,
    isLoading,
  } = useQuery(
    listConsumerPostCheckoutRedeemableTicketsQuery({ postCheckoutId: checkoutId, email, cartId })
  );
  useEffect(() => {
    if (isError) {
      notifications.show({
        message: error ? getErrorMessage_v2(error) : 'Error fetching tickets',
        variant: 'error',
      });
    }
  }, [error, isError]);
  return (
    <TicketsPage
      redeemableTickets={redeemableTickets}
      isLoading={isLoading}
      email={email}
      checkoutId={checkoutId}
      cartId={cartId}
    />
  );
};

/**
 * Loads the tickets page if a checkout id or cart id is provided
 * If neither checkout id nor cart id is provided, it will navigate to the home page and show an error notification
 */
const TicketsPageGuard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const checkoutId = searchParams.get('checkout_id') ?? undefined;
  const email = searchParams.get('email') ?? undefined;
  // referencing and using cart_id when present supports legacy links
  const cartId = searchParams.get('cart_id') ?? undefined;

  // Redirect them to the home page and show an error notification if neither checkout id nor cart id is provided
  // Purposefully using useEffect to group the navigation and notification together
  useEffect(() => {
    if (!checkoutId && !cartId) {
      navigate('/home');
      notifications.show({
        message: 'No order found',
        variant: 'error',
      });
    }
  }, [checkoutId, cartId, navigate]);

  if (!checkoutId && !cartId) {
    return null;
  }

  return <RenderWithPostCheckoutId checkoutId={checkoutId} cartId={cartId} email={email} />;
};

export { TicketsPageGuard as TicketsPage };
